import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { NEARBY_SUBURBS } from "./nearby";
import JsonLd from "./JsonLd";

const useDocumentMeta = ({ title, description, canonicalPath }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.props).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
      if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
    };
    if (description) setMeta('meta[name="description"]', { tag: "meta", props: { name: "description" }, content: description });
    if (canonicalPath) setMeta('link[rel="canonical"]', { tag: "link", props: { rel: "canonical" }, href: `https://passionpilates.au${canonicalPath}` });
  }, [title, description, canonicalPath]);
};

export default function NearbyIndex() {
  useDocumentMeta({
    title: "Reformer Pilates Studios Near Chadstone — 15km Service Area | Passion Pilates",
    description:
      "Passion Pilates serves Malvern East, Oakleigh, Mount Waverley, Carnegie, Ashwood, Glen Iris, Camberwell and every suburb within 15km of Chadstone. Boutique reformer studio · classes from $35.",
    canonicalPath: "/pilates-near",
  });

  // ItemList schema — helps AI engines return the full list of suburbs served.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: NEARBY_SUBURBS.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://passionpilates.au/pilates-near/${s.slug}`,
      name: `Reformer Pilates near ${s.suburb}`,
    })),
  };

  return (
    <div
      className="min-h-screen bg-[#0a0a0a] text-[#E8DCC4]"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      <JsonLd data={jsonLd} />

      <header className="border-b border-[#E8DCC4]/15">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-8 flex items-center justify-between">
          <Link to="/" className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] hover:text-[#E8DCC4] transition-colors">
            ← Home
          </Link>
          <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#E8DCC4]/60">
            Passion Pilates
          </span>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-6">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C97654] mb-5">
          Serving Melbourne's inner south-east · 15km radius
        </p>
        <h1 className="text-[#FBF6EC] text-[38px] md:text-[56px] leading-[1.02] tracking-[-1px] font-normal mb-6">
          Reformer Pilates <em className="italic text-[#C97654]">near you.</em>
        </h1>
        <p className="font-sans text-[17px] md:text-[19px] leading-[1.65] text-[#E8DCC4]/85 max-w-2xl">
          Passion Pilates is a 9-bed boutique reformer studio in Chadstone. We serve members from every
          suburb within 15km — from Malvern East and Oakleigh right through to Camberwell, Notting Hill
          and Burwood. Pick your suburb to see distance, drive time and why locals pick us.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14 border-t border-[#E8DCC4]/15 mt-8">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {NEARBY_SUBURBS.map((s) => (
            <Link
              key={s.slug}
              to={`/pilates-near/${s.slug}`}
              data-testid={`nearby-suburb-${s.slug}`}
              className="group block border border-[#E8DCC4]/15 hover:border-[#C97654] p-6 transition-colors"
            >
              <p className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#C97654] mb-2">
                {s.distanceKm} km · {s.driveMinutes} min
              </p>
              <h3 className="text-[#FBF6EC] text-[22px] leading-tight font-normal tracking-[-0.3px] group-hover:text-[#C97654] transition-colors mb-1">
                {s.suburb}
              </h3>
              <p className="font-sans text-[13px] text-[#E8DCC4]/55">{s.postcode}</p>
            </Link>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#E8DCC4]/15 py-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-sans text-[12px] tracking-[0.2em] uppercase text-[#E8DCC4]/55">Passion Pilates · 5/49 Batesford Rd Chadstone</p>
          <Link to="/" className="font-sans text-[12px] tracking-[0.2em] uppercase font-bold text-[#E8DCC4] border-b-2 border-[#C97654] pb-[2px] hover:text-[#C97654] transition-colors">
            Back to home →
          </Link>
        </div>
      </footer>
    </div>
  );
}
